<template>
  <section class="r-smart-offer-page">
    <ac-data-table
      :headers="headerVisible(headers)"
      :items="headerFilter(items)"
      :item-class="row_class"
      hide-default-footer
      disable-pagination
      sort-by="name"
      item-key="uid"
      fixed-header
      :height="winsize.height - 200"

      :search.sync="search"
      v-model="selected"

      :can-select="item => !row_readonly(item)"
      :can-delete="item => !row_readonly(item)"
      :remove-bulk="bulkRemove"
      :bulk-mode.sync="bulkMode"

      title="Smart Offers"
    >
      <template #actions>
        <v-btn @click="row_create" class="ma-2" color="primary">
          New Offer
        </v-btn>

        <!-- Adjust Time Button - Disabled if none adjustable -->
        <v-btn @click="adjust_time_dialog" class="ma-2" color="orange" :disabled="nonReadonlyCount === 0">
          <v-icon left small>mdi-clock-edit-outline</v-icon>
          Adjust Time
        </v-btn>

        <!-- Quick adjust for selected items -->
        <v-chip
            v-if="selected.length > 0"
            class="ma-2"
            color="orange"
            text-color="white"
            @click="adjust_selected_time"
        >
          {{ selected.length }} selected
          <v-icon right small>mdi-clock-fast</v-icon>
        </v-chip>

        <v-btn @click="show_preview" class="ma-2" color="accent">
          Preview JSON
        </v-btn>
      </template>

      <template v-for="(header,index) in headers" v-slot:[`header.${header.value}`]>
          <span v-if="header.filters" :key="header+index">
            {{ header.text }}
            <v-menu offset-y :close-on-content-click="false">
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon v-bind="attrs" v-on="on">
                  <v-icon small :color="header.filters.length ? 'primary' : ''">
                    mdi-filter-variant
                  </v-icon>
                </v-btn>
              </template>
              <div style="background-color: white; width: 280px">
                <v-list>
                  <v-list-item>
                    <v-autocomplete
                      v-model="header.filters"
                      :items="headerFilterValues(header)"
                      label="Select filters"
                      class="mt-3"
                      chips small-chips
                      auto-select-first
                      multiple dense
                      color="light-blue lighten-3"
                      append-icon="mdi-filter"
                    >
                      <template v-slot:selection="data">
                        <v-chip
                          v-bind="data.attrs"
                          :input-value="data.selected"
                          close small class="ma-1"
                          @click="data.select"
                          @click:close="headerFilterRemove(header,data.item)"
                        >
                          {{ data.item }}
                        </v-chip>
                      </template>
                      <template v-slot:item="data">
                        <v-list-item-content v-text="data.item"/>
                      </template>
                    </v-autocomplete>
                  </v-list-item>
                </v-list>
              </div>
            </v-menu>
          </span>
        <span v-else :key="index+header">
            {{ header.text }}
          </span>
      </template>

      <template v-slot:item.bundles="{item}">
        <div>{{ row_bundles(item) }}</div>
      </template>
      <template v-slot:item.enabled="{item}">
        <div align="center">{{ item.enabled ? "ON" : "OFF" }}</div>
      </template>
      <template v-slot:item.remoteAssets="{item}">
        <div align="center">{{ item.remoteAssets ? "ON" : "OFF" }}</div>
      </template>
      <template v-slot:item.actions="{item}">
        <v-icon v-if="row_readonly(item)" @click="row_view(item)" class="mr-2" small color="green">
          mdi-eye
        </v-icon>
        <v-icon v-else-if="item.locked" @click="row_update(item)" class="mr-2" small color="orange">
          mdi-pencil
        </v-icon>
        <v-icon v-else @click="row_update(item)" class="mr-2" small color="green">
          mdi-pencil
        </v-icon>
        <v-icon @click="row_copy(item)" class="mr-2" small color="teal lighten-1">
          mdi-content-copy
        </v-icon>
        <v-icon :disabled="item.global || item.shared" @click="row_export(item)" class="mr-2" small color="blue">mdi-briefcase-upload</v-icon>
        <v-icon :disabled="row_readonly(item)" @click="row_remove(item)" class="mr-2" small color="red">
          mdi-delete
        </v-icon>
        <v-icon :disabled="item.global || item.shared" @click="row_convert(item)" class="mr-2" small color="blue">mdi-cloud-upload-outline</v-icon>
      </template>

      <template v-slot:no-results>
        <v-alert :value="true" color="blue-grey" dense>
          Your search for "{{ search }}" found no results.
        </v-alert>
      </template>
    </ac-data-table>

    <r-smart-offer-dialog ref="dialog"/>
    <r-dialog-preview-collection ref="previewCollection"/>
    <r-smart-offer-adjust-time-dialog ref="adjustTimeDialog"/>
  </section>
</template>

<script>
import VueTypes from "vue-types";
import {ViewRules} from "@/common/ViewRules";
import {SmartOffer} from "@/modules/redactor/models/smart-offer/core/SmartOffer";
import {RedactorUtils} from "@/modules/redactor/core/RedactorUtils";
import {SmartOfferTimeUtils} from "@/modules/redactor/utils/SmartOfferTimeUtils";
import {getObjectValueByPath} from "vuetify/lib/util/helpers";

import RSmartOfferDialog from "@/modules/redactor/views/smart-offer/r-smart-offer-dialog";
import RSmartOfferAdjustTimeDialog from "@/modules/redactor/views/smart-offer/r-smart-offer-adjust-time-dialog.vue";
import AcDataTable from "@/components/Table/ac-data-table.vue";
import RDialogPreviewCollection from "@/modules/redactor/views/preview/r-preview-collection-dialog.vue";

const NO_LAUNCH_TIME_MESSAGE = "No offers with launch times found.\n\nOnly offers with at least one guardTime rule whose launchTime is greater than 0 can have their launch times adjusted.";

export default {
  components: {RDialogPreviewCollection, RSmartOfferDialog, RSmartOfferAdjustTimeDialog, AcDataTable},
  data: () => ({
    search: "",
    headers: [
      {text: "Name", value: "name"},
      {text: "Kind", value: "kind.name", filters: []},
      {text: "Bundles", value: "bundles"},
      {text: "Button Place", value: "buttonPlace.name", filters: []},
      {text: "Button Priority", value: "buttonPriority", filters: []},
      {text: "Status", value: "enabled"},
      {text: "Remote Assets", value: "remoteAssets", filters: []},
      {text: "UID", value: "uid"},
      {text: "", value: "actions", width: "100%", sortable: false, align: "end"}
    ],
    items: {
      type: VueTypes.arrayOf(SmartOffer)
    },
    selected: [],
    bulkMode: false,
  }),

  computed: {
    masterAccess() { return RedactorUtils.hasMasterAccess(); },
    winsize() { return window.app.winsize; },

    // Count non-readonly offers
    nonReadonlyCount() {
      if (!this.items || this.items.length === 0) return 0;
      return this.items.filter(item => !this.row_readonly(item)).length;
    },

    // Count adjustable offers (non-readonly + has valid launch time)
    adjustableCount() {
      if (!this.items || this.items.length === 0) return 0;
      const nonReadonlyOffers = this.items.filter(item => !this.row_readonly(item));
      return this.filterAdjustableOffers(nonReadonlyOffers).length;
    },

    // Skipped offers among non-readonly
    skippedCount() {
      if (!this.items || this.items.length === 0) return 0;
      const nonReadonlyOffers = this.items.filter(item => !this.row_readonly(item));
      return nonReadonlyOffers.length - this.adjustableCount;
    }
  },

  methods: {
    row_view(item) {
      this.$refs['dialog'].show(ViewRules.READONLY, item.clone()).catch(() => {});
    },
    row_copy(item) {
      this.$refs['dialog'].show(ViewRules.COPY, item.copy()).then(
        result => { RedactorUtils.pushSmartOffer(result).then(() => { this.$alert("Changes was published"); }); },
        () => {}
      );
    },
    row_create() {
      this.$refs['dialog'].show(ViewRules.CREATE, null).then(
        result => { RedactorUtils.pushSmartOffer(result).then(() => { this.$alert("Changes was published"); }); },
        () => {}
      );
    },
    row_update(item) {
      this.$refs['dialog'].show(ViewRules.UPDATE, item.clone()).then(
        result => { RedactorUtils.pushSmartOffer(result).then(() => { this.$alert("Changes was published"); }); },
        () => {}
      );
    },
    row_remove(item) {
      this.$confirm("Are you sure you want to delete this item?").then(() => {
        RedactorUtils.removeSmartOffer(item).then(() => { this.$alert("Changes was published"); });
      });
    },

    bulkRemove(items) {
      return RedactorUtils.removeBulkSmartOffers(items);
    },

    row_export(item) {
      let result = item.clone();
      result.export = !item.export;
      RedactorUtils.pushSmartOffer(result);
    },
    row_convert(item) {
      this.$confirm("Are you sure you want to move this item to global config?")
        .then(() => {
          RedactorUtils.convertSmartOffer(item.clone())
            .then(() => { this.$alert("Changes was published", "Convert To Global", "success"); })
            .catch((error) => { this.$alert(error, "Convert To Global", "error"); });
        });
    },
    row_class(item) {
      if (item.shared) return "yellow lighten-4";
      else if (item.global) return "cyan lighten-4";
      else if (item.export) return "green lighten-3";
      else if (item.changed) return "blue lighten-3";
      else if (!item.enabled) return "blue-grey lighten-3";
    },
    row_readonly(item) {
      return (item.locked && !this.masterAccess) || item.global || item.shared;
    },

    /**
     * Extract the first bundle name from an item
     * @param {Object} item - The item with bundles
     * @returns {string} The first bundle name or empty string
     */
    _getFirstBundleName(item) {
      if (!item.bundles || item.bundles.length === 0) return "";
      const firstBundle = item.bundles[0];
      return firstBundle?.kind?.name || "";
    },

    /**
     * Format bundle display with count
     * @param {string} firstName - The first bundle name
     * @param {number} remainingCount - Count of remaining bundles
     * @returns {string} Formatted bundle display
     */
    _formatBundleDisplay(firstName, remainingCount) {
      const displayName = firstName || "-";
      return remainingCount > 0 ? `${displayName} (+${remainingCount})` : displayName;
    },

    row_bundles(item) {
      const firstName = this._getFirstBundleName(item);
      const bundleCount = item.bundles?.length || 0;
      const remainingCount = Math.max(bundleCount - 1, 0);
      return this._formatBundleDisplay(firstName, remainingCount);
    },

    headerFilter(items) {
      let hh = this.headers.filter(h => h.filters && h.filters.length > 0);
      if (hh.length === 0) return items;
      else {
        return items.filter(item => {
          return hh.every(header => {
            let value = getObjectValueByPath(item, header.value);
            if (!value) return false;
            let text = value.toString().toLocaleLowerCase();
            return header.filters.some(v => text.includes(v.toString().toLocaleLowerCase()));
          });
        });
      }
    },
    headerFilterValues(header) {
      let values = this.items.map(item => getObjectValueByPath(item, header.value));
      let unique = [...new Set(values.filter(v => v))];
      return unique;
    },
    headerFilterRemove(header, filter) {
      const index = header.filters.indexOf(filter);
      if (index >= 0) header.filters.splice(index, 1);
    },
    headerVisible(headers) {
      return headers.filter(h => h.visible === true || h.visible === undefined);
    },
    show_preview() {
      this.$refs['previewCollection'].show(this.items, SmartOffer, "smart-offer")
          .then(() => this.$alert("Changes was published"));
    },

    /**
     * Filter offers to only those with valid launch times
     */
    filterAdjustableOffers(offers) {
      return offers.filter(item => SmartOfferTimeUtils.hasValidLaunchTime(item));
    },

    /**
     * Open adjust time dialog with all offers
     * User can filter inside the dialog
     */
    adjust_time_dialog() {
      // Get all non-readonly offers
      const nonReadonlyOffers = this.items.filter(item => !this.row_readonly(item));

      if (nonReadonlyOffers.length === 0) {
        this.$alert("No adjustable offers found. All offers are either locked or global.", "Adjust Time", "warning");
        return;
      }

      // Filter offers that have valid launch times
      const adjustableOffers = this.filterAdjustableOffers(nonReadonlyOffers);

      if (adjustableOffers.length === 0) {
        this.$alert(NO_LAUNCH_TIME_MESSAGE, "Adjust Time", "warning");
        return;
      }

      // Calculate skipped count
      const filteredCount = nonReadonlyOffers.length - adjustableOffers.length;

      // Open dialog with skip info
      this.openAdjustDialog(adjustableOffers, filteredCount);
    },

    openAdjustDialog(adjustableOffers, skippedCount = 0) {
      this.$refs['adjustTimeDialog'].show(adjustableOffers, skippedCount)
          .then((result) => {
            this.$alert(
                `Successfully adjusted ${result.count} offer(s) using ${result.mode} mode`,
                "Adjust Time",
                "success"
            );
          })
          .catch(() => {
            // User cancelled or error occurred
          });
    },

    /**
     * Quick adjust for selected offers
     */
    adjust_selected_time() {
      if (this.selected.length === 0) {
        this.$alert("No offers selected", "Adjust Time", "warning");
        return;
      }

      // Filter out readonly offers from selection
      const nonReadonlyOffers = this.selected.filter(item => !this.row_readonly(item));

      if (nonReadonlyOffers.length === 0) {
        this.$alert("Selected offers cannot be adjusted (locked or global)", "Adjust Time", "warning");
        return;
      }

      // Filter offers that have valid launch times
      const adjustableOffers = this.filterAdjustableOffers(nonReadonlyOffers);

      if (adjustableOffers.length === 0) {
        this.$alert(NO_LAUNCH_TIME_MESSAGE, "Adjust Time", "warning");
        return;
      }

      // Calculate skipped counts
      const readonlyCount = this.selected.length - nonReadonlyOffers.length;
      const noTimeCount = nonReadonlyOffers.length - adjustableOffers.length;
      const totalSkipped = readonlyCount + noTimeCount;

      // Open dialog with skip info
      this.openSelectedDialog(adjustableOffers, totalSkipped, readonlyCount, noTimeCount);
    },

    openSelectedDialog(adjustableOffers, totalSkipped = 0, readonlyCount = 0, noTimeCount = 0) {
      this.$refs['adjustTimeDialog'].show(adjustableOffers, totalSkipped, readonlyCount, noTimeCount)
          .then((result) => {
            this.$alert(
                `Successfully adjusted ${result.count} offer(s) using ${result.mode} mode`,
                "Adjust Time",
                "success"
            );
            // Clear selection after successful adjustment
            this.selected = [];
          })
          .catch(() => {
            // User cancelled or error occurred
          });
    },
  },
  created() {
    this.items = RedactorUtils.getSmartOffers();
  }
};
</script>

<style>
/* ...existing styles... */
</style>
